import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiCreditCard, FiLock, FiCheckCircle, FiShield, FiAlertCircle } from 'react-icons/fi';
import authService from '../../../../services/auth.service';
import './Login.css';

const CYCLE_LABELS = {
    MENSUAL: 'Mensual',
    SEMESTRAL: 'Semestral',
    ANUAL: 'Anual',
};

const PaymentCheckout = () => {
    const { pagoId } = useParams();
    const navigate = useNavigate();
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    // Simulated card form state
    const [cardNumber, setCardNumber] = useState('');
    const [cardName, setCardName] = useState('');
    const [expiry, setExpiry] = useState('');
    const [cvv, setCvv] = useState('');

    useEffect(() => {
        const fetchOrder = async () => {
            try {
                const data = await authService.getPaymentOrder(pagoId);
                setOrder(data);
            } catch (err) {
                setError('No se pudo cargar la orden de pago.');
            } finally {
                setLoading(false);
            }
        };
        fetchOrder();
    }, [pagoId]);

    const formatCardNumber = (value) => {
        const cleaned = value.replace(/\D/g, '').slice(0, 16);
        return cleaned.replace(/(.{4})/g, '$1 ').trim();
    };

    const formatExpiry = (value) => {
        const cleaned = value.replace(/\D/g, '').slice(0, 4);
        if (cleaned.length >= 3) return `${cleaned.slice(0, 2)}/${cleaned.slice(2)}`;
        return cleaned;
    };

    const handlePayment = async () => {
        // Basic visual validation (simulated — no real card logic)
        if (cardNumber.replace(/\s/g, '').length < 16) {
            setError('Ingresa un número de tarjeta válido');
            return;
        }
        if (!cardName.trim()) {
            setError('Ingresa el nombre del titular');
            return;
        }
        if (expiry.length < 5) {
            setError('Ingresa la fecha de expiración');
            return;
        }
        if (cvv.length < 3) {
            setError('Ingresa el CVV');
            return;
        }

        setError('');
        setProcessing(true);

        // Simulate a brief processing delay for realism
        await new Promise(resolve => setTimeout(resolve, 2000));

        try {
            await authService.confirmSimulatedPayment(pagoId);
            setSuccess(true);
            // Redirigir a página post-registro con detalles
            setTimeout(() => {
                navigate('/registro-exitoso', {
                    state: {
                        isPaid: true,
                        planName: order?.plan_nombre || order?.plan_aplicacion || 'Plan',
                        billingCycle: order?.plan_ciclo || '',
                        amount: order?.monto_total || 0,
                        empresaNombre: order?.empresa_nombre || '',
                    }
                });
            }, 1500);
        } catch (err) {
            setError('Error al procesar el pago. Intenta nuevamente.');
            setProcessing(false);
        }
    };

    const formatCLP = (amount) => new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(amount);

    if (loading) {
        return (
            <div className="login-page-bg">
                <div style={{ textAlign: 'center', color: '#64748b' }}>
                    <div className="spinner" style={{ margin: '0 auto 16px' }}></div>
                    <p>Cargando orden de pago...</p>
                </div>
            </div>
        );
    }

    if (success) {
        return (
            <div className="login-page-bg">
                <div style={{
                    maxWidth: '480px', width: '100%', padding: '40px',
                    background: 'white', borderRadius: '16px',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.1)',
                    textAlign: 'center'
                }}>
                    <div style={{
                        width: '72px', height: '72px', borderRadius: '50%',
                        background: 'linear-gradient(135deg, #10b981, #059669)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        margin: '0 auto 20px',
                        boxShadow: '0 8px 25px rgba(16, 185, 129, 0.3)'
                    }}>
                        <FiCheckCircle size={36} color="white" />
                    </div>
                    <h2 style={{
                        fontSize: '1.5rem', fontWeight: 'bold', color: '#1e293b', marginBottom: '8px'
                    }}>¡Pago Exitoso!</h2>
                    <p style={{ color: '#64748b', fontSize: '0.95rem', marginBottom: '20px' }}>
                        Tu suscripción ha sido activada. Se ha enviado un correo electrónico para verificar tu cuenta.
                    </p>
                    <div style={{
                        background: 'linear-gradient(135deg, rgba(16,185,129,0.08), rgba(5,150,105,0.08))',
                        border: '1px solid rgba(16,185,129,0.2)',
                        borderRadius: '10px', padding: '12px',
                        fontSize: '0.85rem', color: '#047857'
                    }}>
                        📧 Revisa tu bandeja de entrada para completar la verificación.
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="login-page-bg">
            <div style={{
                maxWidth: '520px', width: '100%',
                background: 'white', borderRadius: '16px',
                boxShadow: '0 20px 60px rgba(0,0,0,0.1), 0 0 0 1px rgba(0,0,0,0.03)',
                overflow: 'hidden'
            }}>
                {/* Header */}
                <div style={{
                    background: 'linear-gradient(135deg, #4f46e5, #7c3aed, #ec4899)',
                    padding: '28px 30px',
                    color: 'white',
                    position: 'relative',
                    overflow: 'hidden'
                }}>
                    {/* Decorative circles */}
                    <div style={{
                        position: 'absolute', top: '-20px', right: '-20px',
                        width: '100px', height: '100px', borderRadius: '50%',
                        background: 'rgba(255,255,255,0.08)'
                    }}></div>
                    <div style={{
                        position: 'absolute', bottom: '-30px', left: '30%',
                        width: '80px', height: '80px', borderRadius: '50%',
                        background: 'rgba(255,255,255,0.05)'
                    }}></div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                        <FiLock size={18} />
                        <span style={{ fontSize: '0.85rem', opacity: 0.9 }}>Pago Seguro</span>
                    </div>
                    <h2 style={{ fontSize: '1.4rem', fontWeight: 'bold', margin: 0 }}>Completar Suscripción</h2>
                </div>

                <div style={{ padding: '28px 30px' }}>
                    {/* Order Summary */}
                    {order && (
                        <div style={{
                            border: '2px solid transparent',
                            background: 'linear-gradient(white, white) padding-box, linear-gradient(135deg, #4f46e5, #ec4899) border-box',
                            borderRadius: '12px', padding: '16px', marginBottom: '24px',
                            boxShadow: '0 4px 15px rgba(79, 70, 229, 0.08)'
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                <span style={{ fontSize: '0.82rem', color: '#64748b', fontWeight: 500 }}>Plan</span>
                                <span style={{ fontSize: '0.82rem', color: '#64748b' }}>{CYCLE_LABELS[order.plan_ciclo] || order.plan_ciclo}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                <strong style={{ color: '#1e293b', fontSize: '1rem' }}>{order.plan_nombre || order.plan_aplicacion}</strong>
                                <span style={{
                                    fontWeight: 'bold', fontSize: '1.15rem',
                                    background: 'linear-gradient(135deg, #4f46e5, #ec4899)',
                                    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                                    backgroundClip: 'text'
                                }}>
                                    {formatCLP(order.monto_total)}
                                </span>
                            </div>
                            <div style={{
                                borderTop: '1px solid rgba(79,70,229,0.1)',
                                paddingTop: '8px', fontSize: '0.8rem', color: '#64748b'
                            }}>
                                Empresa: {order.empresa_nombre}
                            </div>
                        </div>
                    )}

                    {/* Card Form */}
                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#334155', marginBottom: '6px' }}>
                            <FiCreditCard size={14} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                            Número de Tarjeta
                        </label>
                        <input
                            type="text"
                            className="form-control-custom"
                            placeholder="4242 4242 4242 4242"
                            value={cardNumber}
                            onChange={e => setCardNumber(formatCardNumber(e.target.value))}
                            maxLength={19}
                            style={{ fontSize: '1rem', letterSpacing: '2px', fontFamily: 'monospace' }}
                        />
                    </div>

                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#334155', marginBottom: '6px' }}>
                            Nombre del Titular
                        </label>
                        <input
                            type="text"
                            className="form-control-custom"
                            placeholder="Como aparece en la tarjeta"
                            value={cardName}
                            onChange={e => setCardName(e.target.value.toUpperCase())}
                            style={{ textTransform: 'uppercase' }}
                        />
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
                        <div>
                            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#334155', marginBottom: '6px' }}>
                                Expiración
                            </label>
                            <input
                                type="text"
                                className="form-control-custom"
                                placeholder="MM/AA"
                                value={expiry}
                                onChange={e => setExpiry(formatExpiry(e.target.value))}
                                maxLength={5}
                                style={{ fontFamily: 'monospace', letterSpacing: '2px' }}
                            />
                        </div>
                        <div>
                            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#334155', marginBottom: '6px' }}>
                                CVV
                            </label>
                            <input
                                type="password"
                                className="form-control-custom"
                                placeholder="•••"
                                value={cvv}
                                onChange={e => setCvv(e.target.value.replace(/\D/g, '').slice(0, 4))}
                                maxLength={4}
                                style={{ fontFamily: 'monospace', letterSpacing: '4px' }}
                            />
                        </div>
                    </div>

                    {/* Error */}
                    {error && (
                        <div style={{
                            borderRadius: '10px', border: '1px solid rgba(239,68,68,0.2)',
                            background: 'rgba(239,68,68,0.05)', padding: '10px 14px',
                            marginBottom: '16px', fontSize: '0.85rem', color: '#dc2626',
                            display: 'flex', alignItems: 'center', gap: '8px'
                        }}>
                            <FiAlertCircle size={16} /> {error}
                        </div>
                    )}

                    {/* Pay Button */}
                    <button
                        onClick={handlePayment}
                        disabled={processing}
                        style={{
                            width: '100%', padding: '14px',
                            background: processing
                                ? 'linear-gradient(135deg, #94a3b8, #64748b)'
                                : 'linear-gradient(135deg, #4f46e5, #ec4899)',
                            color: 'white', border: 'none', borderRadius: '10px',
                            fontSize: '1rem', fontWeight: 'bold', cursor: processing ? 'wait' : 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
                            boxShadow: processing ? 'none' : '0 4px 20px rgba(79, 70, 229, 0.35)',
                            transition: 'all 0.3s ease',
                            letterSpacing: '0.5px'
                        }}
                    >
                        {processing ? (
                            <>
                                <div style={{
                                    width: '18px', height: '18px', border: '2px solid rgba(255,255,255,0.3)',
                                    borderTop: '2px solid white', borderRadius: '50%',
                                    animation: 'spin 0.8s linear infinite'
                                }}></div>
                                Procesando Pago...
                            </>
                        ) : (
                            <>
                                <FiLock size={18} />
                                Pagar {order ? formatCLP(order.monto_total) : ''}
                            </>
                        )}
                    </button>

                    {/* Security badges */}
                    <div style={{
                        display: 'flex', justifyContent: 'center', gap: '20px',
                        marginTop: '20px', paddingTop: '16px',
                        borderTop: '1px solid #f1f5f9'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.75rem', color: '#94a3b8' }}>
                            <FiShield size={14} /> Conexión segura
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.75rem', color: '#94a3b8' }}>
                            <FiLock size={14} /> Datos encriptados
                        </div>
                    </div>

                    {/* Simulated notice — remove in production */}
                    <div style={{
                        marginTop: '16px', padding: '10px 14px',
                        background: 'linear-gradient(135deg, rgba(245,158,11,0.06), rgba(217,119,6,0.06))',
                        border: '1px solid rgba(245,158,11,0.15)',
                        borderRadius: '8px', fontSize: '0.75rem', color: '#92400e',
                        textAlign: 'center'
                    }}>
                        🧪 <strong>Entorno de prueba</strong> — No se procesará un cobro real. Usa cualquier dato de tarjeta.
                    </div>
                </div>
            </div>

            <style>{`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
};

export default PaymentCheckout;
